//
// Copyright ArangoDB GmbH, Cologne, Germany
// All rights reserved. See LICENSE.md in the project root for license information.
//

import { Box, Heading, Table, Tbody, Td, Text, Tfoot, Th, Thead, Tr } from "@chakra-ui/react";
import React from "react";
import { CreditUsageBucket } from "../../../api/usage/v1/usage";

const formatCredits = (value: number): string => value.toFixed(2);

// buildBreakdownRows reduces buckets down to one row per component,
// regardless of how many buckets share that component (e.g. one per period,
// or one per period per deployment). Task 8 currently calls this component
// with GRANULARITY_TOTAL + group_by_component=true, which already yields one
// bucket per component - but nothing in the `buckets: CreditUsageBucket[]`
// prop type encodes that contract, and a future caller passing multi-period
// or ungrouped data would otherwise get the same component split across
// several rows while the separately-supplied totalCredits silently disagreed
// with their sum. Reducing here makes that mismatch impossible regardless of
// what the caller passes. For today's caller this produces byte-identical
// output to mapping buckets 1:1.
export const buildBreakdownRows = (buckets: CreditUsageBucket[]): { component: string; credits: number }[] => {
  const creditsByComponent = new Map<string, number>();
  buckets.forEach((bucket) => {
    if (!bucket.component) {
      return;
    }
    creditsByComponent.set(bucket.component, (creditsByComponent.get(bucket.component) || 0) + (bucket.credits || 0));
  });

  return Array.from(creditsByComponent.entries())
    .map(([component, credits]) => ({ component, credits }))
    .sort((a, b) => b.credits - a.credits);
};

export const CreditUsageBreakdown = ({
  buckets,
  totalCredits,
  hideHeading = false,
}: {
  buckets: CreditUsageBucket[];
  totalCredits: number;
  // The caller renders its own "Usage by component" heading instead (e.g. to
  // add a "clear" control next to it once a single day is selected).
  hideHeading?: boolean;
}) => {
  const rows = buildBreakdownRows(buckets);

  if (!rows.length) {
    // hideHeading callers already committed to rendering a heading for this
    // section unconditionally (see OrganizationUsageView), so returning null
    // here would leave that heading floating above nothing with no
    // indication of why - e.g. a clicked day that genuinely had no usage.
    // The original (hideHeading=false) callers own their heading themselves,
    // so returning null there is unchanged: no heading, no empty state, same
    // as before this component supported a clicked-day drill-down at all.
    return hideHeading ? (
      <Text paddingTop="8px" color="gray.600">
        No usage recorded for this period.
      </Text>
    ) : null;
  }

  return (
    // No top padding when the caller supplies its own heading: that heading
    // row already carries the equivalent spacing (see OrganizationUsageView),
    // so adding this box's own would double it.
    <Box paddingTop={hideHeading ? undefined : "32px"}>
      {!hideHeading && (
        <Heading size="sm" paddingBottom="8px">
          Usage by component
        </Heading>
      )}
      <Table size="sm">
        <Thead>
          <Tr>
            <Th>Component</Th>
            <Th isNumeric>Credits</Th>
          </Tr>
        </Thead>
        <Tbody>
          {rows.map((row) => (
            <Tr key={row.component}>
              <Td>{row.component}</Td>
              <Td isNumeric>{formatCredits(row.credits)}</Td>
            </Tr>
          ))}
        </Tbody>
        <Tfoot>
          <Tr>
            <Td fontWeight="bold" borderBottomWidth={0}>
              Total
            </Td>
            <Td isNumeric fontWeight="bold" borderBottomWidth={0}>
              {formatCredits(totalCredits)}
            </Td>
          </Tr>
        </Tfoot>
      </Table>
    </Box>
  );
};
